import { useEffect } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FadeUp } from "@/components/motion";
import { SERVICE_FAQS } from "@/data/content";

export default function FAQSection({ slug }) {
  const faqs = SERVICE_FAQS[slug] || [];

  useEffect(() => {
    const id = "faq-jsonld";
    document.getElementById(id)?.remove();
    if (faqs.length) {
      const s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
      document.head.appendChild(s);
    }
    return () => document.getElementById(id)?.remove();
  }, [slug]);

  if (!faqs.length) return null;

  return (
    <section data-testid={`faq-section-${slug}`} className="border-t border-slate-200 bg-slate-50 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-8">
        <FadeUp className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-amber-400">
            <MessageCircleQuestion className="h-5 w-5" />
          </span>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">
            Questions, answered.
          </h2>
        </FadeUp>
        <FadeUp delay={0.1} className="mt-8">
          <Accordion type="single" collapsible className="w-full" data-testid={`faq-accordion-${slug}`}>
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${slug}-${i}`}
                className="rounded-xl border border-slate-200 bg-white px-6 mb-3">
                <AccordionTrigger className="text-left font-display text-base font-bold text-navy hover:text-amber-600">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm leading-relaxed text-slate-600 pb-1">{f.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>
      </div>
    </section>
  );
}
